<script>
    import ViewLogs from "../ViewLogs.svelte";
    import ViewNotes from "../ViewNotes.svelte";
    import PeopleChooser from "../PeopleChooser.svelte";
    import CompanyChooser from "../CompanyChooser.svelte";
    import {PAGE_ID, router, navigateTo, navigateWithContextTo} from "../../stores/router";
    import {app_state, displayDeal, selectDeal, updateDeal, selectPromotion, selectCompany, filemanager} from "../../stores/misc";
    import {
        I_COMPANY,
        I_DEAL,
        getNullDeal,
        dealAsObject,
        DEAL_LABELS,
        DEAL_EXPLANATION, DATATYPE
    } from "../../../../../../lib/datatypes";
    import {deepCopyByValue, prettyHTMLFromPlainText} from "../../../../../../lib/utils";
    import {getTimeDiffAsShortText, now, toDate} from "../../../../../../lib/dates_times";
    import {companies} from "../../stores/companies";
    import {promotions} from "../../stores/promotions";
    import PromotionsList from "../PromotionsList.svelte";
    import Label from "../Label.svelte";
    import RelatedPeopleList from "../RelatedPeopleList.svelte";
    import RelatedCompaniesList from "../RelatedCompaniesList.svelte";
    import FileAttachmentList from "../FileAttachmentList.svelte";
    import NextAction from "../NextAction.svelte";


    // 'Empty' display person for use when there is no selected person
    let empty_deal_obj = dealAsObject(getNullDeal());

    $: selected_deal = $app_state.selected_deal_id === null ? empty_deal_obj : displayDeal($app_state.selected_deal_id);

    $: related_people = selected_deal !== null ? selected_deal.related.filter(row => row.type === DATATYPE.PERSON) : [];
    $: related_companies = selected_deal !== null ? selected_deal.related.filter(row => row.type === DATATYPE.COMPANY) : [];

    let edited_selected_deal = null;
    let notes = [];
    let logs = [];
    const resetStateParams = () => {
        edited_selected_deal = null;
        notes = [];
        logs = [];
    };
    const loadNotesAndLogs = async () => {
        const response = await fetch(`/api/deal/${$app_state.selected_deal_id}`);
        const result = await response.json();
        notes = result.notes;
        logs = result.log;
    };
    $: (async () => {
        if($app_state.selected_deal_id === null) {
            resetStateParams();
        } else {
            await loadNotesAndLogs()
        }
    })()

    const VIEW_STATE = {
        VIEWING: 0,
        EDITING: 1
    };
    let view_state = VIEW_STATE.VIEWING;

    const TAB_STATE = {
        PROMOTIONS: 0,
        NOTES: 1,
        LOG: 2
    };
    // let tab_state = TAB_STATE.PROMOTIONS;
    let tab_state = TAB_STATE.NOTES;

    const onEditDealClick = async () => {
        edited_selected_deal = await deepCopyByValue(selected_deal);
        // console.log(edited_selected_deal);
        view_state = VIEW_STATE.EDITING;
    };

    $: edited_related_people = edited_selected_deal !== null ? edited_selected_deal.related.filter(row => row.type === DATATYPE.PERSON) : [];
    $: edited_related_companies = edited_selected_deal !== null ? edited_selected_deal.related.filter(row => row.type === DATATYPE.COMPANY) : [];

    let show_related_person_search = false;
    let show_related_company_search = false;

    const onRelatedClick = (type, id) => {
        if(edited_selected_deal !== null) {
            edited_selected_deal.related.push({type: type, id:id});
            edited_selected_deal = edited_selected_deal;
        }
        else {
            console.warn(`no deal being edited, why was this person/company clicked?`);
        }
        show_related_person_search = false;
        show_related_company_search = false;
    };

    const resetEditingDeal = () => {
        view_state = VIEW_STATE.VIEWING;
        edited_selected_deal = null;
    }
    const saveEditedDeal = async () => {
        if(edited_selected_deal === null) console.warn('Trying to save a null edited deal!');
        let saved_deal = await updateDeal(edited_selected_deal);
        selectDeal(saved_deal[I_DEAL.ID]); // ..to reflect the changes made (otherwise doesn't pick it up)
        resetEditingDeal();
    };

    const onPromotionClick = id => {
        selectPromotion(id);
        navigateTo(PAGE_ID.VIEW_PROMOTION, PAGE_ID.VIEW_DEAL);
    };

    const onNextActionUpdated = async (e) => {
        const new_next_action = e.detail.new_next_action;
        // NB taking a full copy of the request and then going through the existing saveEditedRequest path..
        edited_selected_deal = await deepCopyByValue(selected_deal);
        edited_selected_deal.next_action = new_next_action;
        await saveEditedDeal();
    };

    let is_uploading_files = false;
    const doChooseFile = () => {
        filemanager.chooseFiles();
        is_uploading_files = true;
    };
    $: {
        if (is_uploading_files) {
            if ($filemanager.files.length > 0) {
                if (edited_selected_deal !== null) {
                    edited_selected_deal.files = edited_selected_deal.files.concat($filemanager.files);
                    edited_selected_deal = edited_selected_deal;
                    is_uploading_files = false;
                    filemanager.resetFiles();
                    console.log('files added, full list =', edited_selected_deal.files);
                }
            }
        }
    }

    const onBackClick = () => {
        if($router.context.previous_page_id !== null) {
            navigateTo($router.context.previous_page_id);
        }
        else {
            navigateTo(PAGE_ID.PROMOTIONS);
        }
    };
</script>


<div class="absolute top-16 right-0 bottom-0 left-0 overflow-y-auto" class:hidden={$router.current_page !== PAGE_ID.VIEW_DEAL}>

    <!-- Header -->
    <div class="px-4 sm:px-6 lg:px-8">

        <!--<Breadcrumbs></Breadcrumbs>-->

        <div class="h-16 flex items-center justify-between">
            <a on:click|preventDefault={onBackClick} href="/" class="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700">
                <svg class="-ml-1 mr-1 h-5 w-5 flex-shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clip-rule="evenodd" />
                </svg>
                Back
            </a>
            <div class="flex flex-shrink-0 md:mt-0 md:ml-4">
                <button on:click={onEditDealClick}
                        type="button" class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">Edit</button>
                <!--<button type="button" class="ml-3 inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">Publish</button>-->
            </div>
        </div>

    </div>

    <div class="px-4 sm:px-6 lg:px-8">
        {#if view_state === VIEW_STATE.VIEWING}

            <div class="flex justify-between">
                <!-- Deal pill-->
                <div class="flex inline-flex items-center px-2 py-1 rounded-full ring-1 ring-inset ring-gray-300">
                    <svg class="h-4 w-4 text-pink-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.732 6.232a2.5 2.5 0 013.536 0 .75.75 0 101.06-1.06A4 4 0 006.5 8v.165c0 .364.034.728.1 1.085h-.35a.75.75 0 000 1.5h.737a5.25 5.25 0 01-.367 3.072l-.055.123a.75.75 0 00.848 1.037l1.272-.283a3.493 3.493 0 011.604.021 4.992 4.992 0 002.422 0l.97-.242a.75.75 0 00-.363-1.456l-.971.243a3.491 3.491 0 01-1.694 0 4.992 4.992 0 00-2.258-.038c.19-.811.227-1.651.111-2.477H9.75a.75.75 0 000-1.5H8.136A4.397 4.397 0 018 8.165V8c0-.641.244-1.28.732-1.768z" clip-rule="evenodd" />
                    </svg>
                    <p class="ml-1 text-sm uppercase font-light text-gray-600">Deal</p>
                </div>

                <!-- Created date -->
                <div class="flex">
                    <div class="flex items-center text-sm text-gray-500">
                        <svg class="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clip-rule="evenodd" />
                        </svg>
                        <p>Created
                            <time datetime="{selected_deal.created}">{getTimeDiffAsShortText(now().valueOf() - toDate(selected_deal.created))}</time>
                        </p>
                    </div>
                </div>
            </div>

            <div class="mt-4 flex items-baseline">
                <p class="text-sm text-gray-500 mr-2 align-baseline">With:</p>
                <span class="inline-flex align-text-bottom"><Label type={'company'}></Label></span>
                <h1
                        on:click={() => {
                            selectCompany(selected_deal.id_company);
                            navigateTo(PAGE_ID.VIEW_COMPANY, PAGE_ID.VIEW_DEAL);
                        }}
                        class="ml-2 text-lg font-medium text-gray-900 hover:text-indigo-500 cursor-pointer">
                    {selected_deal.id_company === '' ? '' : $companies.lookup[selected_deal.id_company][I_COMPANY.NAME]}
                </h1>
            </div>

            <p class="mt-4 font-semibold text-gray-900">
                {@html selected_deal.title}
            </p>
            <p class="mt-4 text-gray-900">
                {@html prettyHTMLFromPlainText(selected_deal.description)}
            </p>

            <NextAction type="deal" owner_id={selected_deal.id} next_action={selected_deal.next_action}
                        on:nextActionUpdate={onNextActionUpdated}
                        on:logsChanged={loadNotesAndLogs}></NextAction>

            {#if related_people.length > 0}
<!--                <p class="mt-4 text-sm text-gray-500">Related People:</p>-->
                <RelatedPeopleList related_people={related_people} return_page_id={PAGE_ID.VIEW_DEAL}></RelatedPeopleList>
            {/if}

            {#if related_companies.length > 0}
<!--                <p class="mt-4 text-sm text-gray-500">Related Companies:</p>-->
                <RelatedCompaniesList related_companies={related_companies} return_page_id={PAGE_ID.VIEW_DEAL}></RelatedCompaniesList>
            {/if}

            {#if selected_deal.files.length > 0}
                <div class="my-4">
<!--                    <p class="mt-4 text-sm text-gray-500">Files:</p>-->
                    <FileAttachmentList files={selected_deal.files}></FileAttachmentList>
                </div>
            {/if}

        {/if}
        {#if view_state === VIEW_STATE.EDITING}
            <p class="text-sm font-medium text-gray-600">{@html DEAL_EXPLANATION}</p>

            <div class="mt-4">
                <label for="request-title" class="block text-sm font-medium text-gray-900">Title</label>
                <input bind:value="{edited_selected_deal.title}" type="text" name="request-title" id="request-title" autocomplete="request-title" class="mt-1 block w-full rounded-md border-gray-300 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
                <p class="mt-3 text-sm italic text-gray-500">({@html DEAL_LABELS.TITLE})</p>
            </div>

            <div class="mt-4">
                <label for="description" class="block text-sm font-medium text-gray-900">Description</label>
                <div class="mt-1">
                    <textarea bind:value={edited_selected_deal.description}
                              id="description" name="description" rows="4"
                              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"></textarea>
                </div>
                <p class="mt-3 text-sm italic text-gray-500">({@html DEAL_LABELS.DESCRIPTION})</p>
            </div>

            <div class="mt-4">
                <p class="block text-sm font-medium text-gray-900">Related People</p>
                <RelatedPeopleList related_people={edited_related_people} return_page_id={PAGE_ID.VIEW_DEAL}></RelatedPeopleList>
                {#if !show_related_person_search}
                    <button on:click={() => show_related_person_search = true}
                            type="button"
                            class="mt-2 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100"
                    >Add Related Person</button>
                {:else}
                    <PeopleChooser
                            type="box"
                            current_search_str=""
                            minimise_box_without_search={true}
                            allow_adding_people={false}
                            on:personClicked={(e) => onRelatedClick(DATATYPE.PERSON, e.detail.id)}
                            on:cancelClicked={() => show_related_person_search = false}></PeopleChooser>
                {/if}
            </div>

            <div class="mt-4">
                <p class="block text-sm font-medium text-gray-900">Related Companies</p>
                <RelatedCompaniesList related_companies={edited_related_companies} return_page_id={PAGE_ID.VIEW_DEAL}></RelatedCompaniesList>
                {#if !show_related_company_search}
                    <button on:click={() => show_related_company_search = true}
                            type="button"
                            class="mt-2 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100"
                    >Add Related Company</button>
                {:else}
                    <CompanyChooser
                            type="box"
                            current_search_str=""
                            minimise_box_without_search={true}
                            allow_adding_company={false}
                            on:companyClicked={(e) => onRelatedClick(DATATYPE.COMPANY, e.detail.id)}
                            on:cancelClicked={() => show_related_company_search = false}></CompanyChooser>
                {/if}
            </div>

            <div class="my-4">
                <p class="block text-sm font-medium text-gray-900">Files</p>
                {#if edited_selected_deal.files.length > 0}
                    <div class="mt-2">
                        <FileAttachmentList files={edited_selected_deal.files}></FileAttachmentList>
                    </div>
                {/if}
                {#if $filemanager.is_uploading}
                    <div class="flex items-center justify-center rounded-md border border-transparent bg-orange-600 px-2 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    ><svg class="animate-spin self-center w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                        Sending..</div>
                {/if}
                <button on:click={doChooseFile}
                        type="button"
                        class="mt-2 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100"
                >Add File</button>
            </div>

            <div class="flex justify-end pt-8">
                <button on:click={resetEditingDeal}
                        type="button"
                        class="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >Cancel</button>
                <button on:click={saveEditedDeal}
                        type="submit"
                        class="ml-3 inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >Save</button>
            </div>
        {/if}
    </div>

    <div class="block mt-6">
        <div class="border-b border-gray-200">
            <nav class="-mb-px px-4 flex justify-between sm:justify-start space-x-8" aria-label="Tabs">
<!--                <a href="/" on:click|preventDefault={() => tab_state = TAB_STATE.PROMOTIONS} class="{tab_state === TAB_STATE.PROMOTIONS ? `border-indigo-500 text-indigo-600` : `border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700` } group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium">-->
<!--                    <svg class="{tab_state === TAB_STATE.PROMOTIONS ? `text-indigo-500` : `text-gray-400 group-hover:text-gray-500`} -ml-0.5 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">-->
<!--                        <path stroke-linecap="round" stroke-linejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />-->
<!--                    </svg>-->
<!--                    <span>Promotions</span>-->
<!--                </a>-->

                <a href="/" on:click|preventDefault={() => tab_state = TAB_STATE.NOTES} class="{tab_state === TAB_STATE.NOTES ? `border-indigo-500 text-indigo-600` : `border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700` } group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium">
                    <svg class="{tab_state === TAB_STATE.NOTES ? `text-indigo-500` : `text-gray-400 group-hover:text-gray-500`} -ml-0.5 mr-2 h-5 w-5"  fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                    </svg>
                    <span>Notes</span>
                </a>

                <a href="/" on:click|preventDefault={() => tab_state = TAB_STATE.LOG} class="{tab_state === TAB_STATE.LOG ? `border-indigo-500 text-indigo-600` : `border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700` } group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium" aria-current="page">
                    <svg class="{tab_state === TAB_STATE.LOG ? `text-indigo-500` : `text-gray-400 group-hover:text-gray-500`} -ml-0.5 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                    </svg>
                    <span>Log</span>
                </a>
            </nav>
        </div>
    </div>

    {#if tab_state === TAB_STATE.PROMOTIONS}
        <div class="px-4 sm:px-6 lg:px-8">

            <PromotionsList
                    promotions_arr_list={selected_deal.list_promotions.map(id => $promotions.lookup[id])}
                    on:promotionClicked={e => onPromotionClick(e.detail.id)}></PromotionsList>

            <div class="h-16 flex items-center justify-end">
                <div class="flex flex-shrink-0">
                    <button on:click={() => navigateWithContextTo(PAGE_ID.ADD_PROMOTION, { id_deal: selected_deal.id }, PAGE_ID.VIEW_DEAL)}
                            type="button" class="relative inline-flex items-center rounded-md border border-transparent bg-indigo-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-800">
                        <!-- Heroicon name: mini/plus -->
                        <svg class="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                        </svg>
                        <span>Add Promotion</span>
                    </button>
                </div>
            </div>

        </div>
    {/if}

    {#if tab_state === TAB_STATE.NOTES}
        <ViewNotes type="deal" owner_id={selected_deal.id} bind:notes></ViewNotes>
    {/if}

    {#if tab_state === TAB_STATE.LOG}
    <ViewLogs type="deal" owner_id={selected_deal.id} bind:logs></ViewLogs>
    {/if}

</div>


<style></style>